import React from 'react'
import { motion } from 'framer-motion'
import { Check, ArrowRight, ChevronDown } from 'lucide-react'
import { HERO_CONTENT, IMAGES } from '../constants'
import { textRevealVariants } from '../utils/animations'

/**
 * HeroRealEstate - Full-viewport hero section for luxury real estate
 * Features headline, bullets, and dual CTAs with modern design
 */

const HeroRealEstate: React.FC = () => {
  return (
    <section
      id="inicio"
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Propiedades de lujo en República Dominicana"
          className="h-full w-full object-cover"
        />
        {/* Gradient Overlay - Black to transparent */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-32 sm:px-6 md:py-40 lg:px-8">
        <div className="max-w-4xl">
          {/* Accent Line */}
          <motion.div
            className="mb-8 h-1 w-20 bg-accent"
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* Headline */}
          <motion.h1
            className="mb-6 text-2xl font-extrabold leading-tight tracking-tight text-white md:text-3xl lg:text-4xl"
            variants={textRevealVariants}
            initial="hidden"
            animate="visible"
          >
            {HERO_CONTENT.headline}
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            className="mb-8 max-w-3xl text-lg font-light leading-relaxed text-white/90 md:text-xl"
            variants={textRevealVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
          >
            {HERO_CONTENT.subheadline}
          </motion.p>

          {/* Bullet Points */}
          <motion.ul
            className="mb-10 space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {HERO_CONTENT.bullets.map((bullet, index) => (
              <li key={index} className="flex items-center text-white">
                <Check
                  className="mr-3 h-6 w-6 flex-shrink-0 text-accent-light"
                  aria-hidden={true}
                />
                <span className="text-base md:text-lg">{bullet}</span>
              </li>
            ))}
          </motion.ul>

          {/* CTA Row */}
          <motion.div
            className="flex flex-col gap-4 sm:flex-row"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Primary CTA */}
            <a
              href="#contacto"
              className="animate-pulse-glow group inline-flex items-center justify-center rounded-lg bg-white px-8 py-4 font-semibold text-black transition-all duration-300 hover:bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              {HERO_CONTENT.ctaPrimary}
              <ArrowRight className="btn-arrow ml-2 h-5 w-5" aria-hidden={true} />
            </a>

            {/* Secondary CTA */}
            <a
              href="#contacto"
              className="group inline-flex items-center justify-center rounded-lg border-2 border-white px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              {HERO_CONTENT.ctaSecondary}
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 transform"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1, ease: [0.22, 1, 0.36, 1] }}
      >
        <a
          href="#contacto"
          className="flex flex-col items-center rounded-lg p-2 text-white/80 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label="Desplazarse a contacto"
        >
          <span className="mb-2 hidden text-sm sm:block">Contacto</span>
          <ChevronDown className="animate-bounce-subtle h-6 w-6" aria-hidden={true} />
        </a>
      </motion.div>
    </section>
  )
}

export default HeroRealEstate
